<template>
  <div id="adventure-gallery">
    <AppHeader v-if="!sharedEvent">{{ gallery ? gallery.title : 'Adventure Gallery' }}</AppHeader>

    <div class="content-container gallery-shell">
      <div class="content">
        <EventRenderer
          v-if="sharedEvent"
          :event="sharedEvent"
        />

        <v-container v-else-if="gallery">
          <v-btn
            outlined
            class="back-link mb-6"
            :to="{ name: 'reckon-way' }"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            The Reckon Way
          </v-btn>

          <v-img
            :src="gallery.heroImage | mediaSrc"
            :aspect-ratio="gallery.heroAspectRatio || 1.5"
            class="gallery-hero"
            :contain="gallery.containHero === true"
          ></v-img>

          <div class="gallery-intro">
            <div class="kicker">{{ gallery.meta }}</div>
            <h1 class="page-title">{{ gallery.title }}</h1>
            <p class="page-copy">{{ gallery.description }}</p>
            <div
              v-if="gallery.purpose"
              class="trip-purpose"
            >
              <strong>Purpose</strong>
              <p>{{ gallery.purpose }}</p>
            </div>
            <div
              v-if="atAGlanceItems.length"
              class="at-a-glance-grid"
              aria-label="At a glance"
            >
              <div
                v-for="item in atAGlanceItems"
                :key="item.key"
                class="at-a-glance-item"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
            <div class="gallery-actions">
              <v-btn
                v-if="storiesComingSoon || inlineBlogPost || blogLinks.length"
                outlined
                class="action-button"
                @click="scrollToStories"
              >
                <v-icon left>{{ storyActionIcon }}</v-icon>
                {{ storyActionLabel }}
              </v-btn>

              <v-btn
                outlined
                class="action-button"
                @click="scrollToGallery"
              >
                <v-icon left>mdi-image-multiple</v-icon>
                Photo Gallery
              </v-btn>
            </div>
          </div>

          <section
            v-if="storiesComingSoon"
            ref="storyIndex"
            class="story-index story-coming-soon"
            aria-labelledby="story-coming-soon-title"
          >
            <div class="kicker">Travel Stories</div>
            <h2 id="story-coming-soon-title" class="section-title">
              {{ gallery.storyComingSoonTitle || 'Stories Coming Soon' }}
            </h2>
            <p class="story-coming-soon-copy">
              {{ gallery.storyComingSoonCopy || 'Longer stories from this adventure will be added soon.' }}
            </p>
          </section>

          <section
            v-if="!storiesComingSoon && !inlineBlogPost && blogLinks.length"
            ref="storyIndex"
            class="story-index"
            aria-labelledby="story-index-title"
          >
            <div class="kicker">Travel Stories</div>
            <h2 id="story-index-title" class="section-title">Stories from {{ gallery.title }}</h2>

            <div class="story-card-grid">
              <router-link
                v-for="link in blogLinks"
                :key="link.slug"
                :to="link.route"
                class="story-card"
              >
                <div class="story-card-kicker">Story {{ link.index + 1 }}</div>
                <h3 class="story-card-title">{{ link.title }}</h3>
                <p class="story-card-copy">{{ link.lede }}</p>
                <div class="story-card-action">
                  Read Story
                  <v-icon small>mdi-arrow-right</v-icon>
                </div>
              </router-link>
            </div>
          </section>

          <section
            v-if="!storiesComingSoon && inlineBlogPost"
            ref="storyContent"
            class="inline-story"
            aria-label="Trip story"
          >
            <TravelBlogArticle
              :post="inlineBlogPost"
              :kicker="`${gallery.meta} Story`"
            />
          </section>

          <section
            v-if="gallery.tripCard"
            class="trip-details-section"
            aria-labelledby="trip-details-title"
          >
            <h2 id="trip-details-title" class="section-title">Trip Details</h2>
            <TripCard
              v-bind="gallery.tripCard"
              class="gallery-trip-card"
              :tags="gallery.tags || []"
              :link-enabled="false"
              :show-image="false"
              :show-cta="false"
              variant="featured"
            />
          </section>

          <section
            v-if="hasExperienceMetadata"
            class="experience-taxonomy"
            aria-labelledby="experience-taxonomy-title"
          >
            <div class="kicker">Experience Tags</div>
            <h2 id="experience-taxonomy-title" class="section-title">Experience DNA</h2>

            <div
              v-if="experienceDnaScores.length"
              class="experience-dna-card"
            >
              <h3>Category Mix</h3>
              <div class="dna-score-grid" aria-label="Experience DNA scores">
                <div
                  v-for="score in experienceDnaScores"
                  :key="score.key"
                  class="dna-score"
                >
                  <div class="dna-score-row">
                    <span>{{ score.label }}</span>
                    <strong>{{ score.value }}</strong>
                  </div>
                  <div class="dna-score-track" aria-hidden="true">
                    <span :style="{ width: score.width }"></span>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="gallery.familyFriendly"
              class="family-note-card"
            >
              <v-icon>mdi-human-male-female-child</v-icon>
              <div>
                <h3>Family-Friendly Adventure</h3>
                <p>
                  {{ gallery.familyNote || 'Loved ones were invited to join this adventure.' }}
                </p>
              </div>
            </div>

            <div
              v-if="gallery.tags && gallery.tags.length"
              class="experience-group"
            >
              <h3>Tags</h3>
              <div class="tag-chip-list" aria-label="Trip tags">
                <span
                  v-for="tag in gallery.tags"
                  :key="tag.slug"
                >
                  <TagPill
                    :label="tag.label"
                    :category="tag.category"
                    :to="{ name: 'reckon-way-experience', params: { tagSlug: tag.slug } }"
                  />
                </span>
              </div>
            </div>

            <div
              v-if="gallery.attendees && gallery.attendees.length"
              class="experience-group"
            >
              <h3>The Reckon Crew</h3>
              <div class="attendee-grid">
                <router-link
                  v-for="attendee in gallery.attendees"
                  :key="attendee.employeeSlug"
                  :to="passportRoute(attendee.employeeSlug)"
                  class="attendee-card"
                >
                  <span>{{ attendee.displayName }}</span>
                  <strong>{{ attendee.role }}</strong>
                  <small>
                    View Passport
                    <v-icon small>mdi-arrow-right</v-icon>
                  </small>
                </router-link>
              </div>
            </div>

            <div class="experience-grid">
              <div
                v-if="gallery.achievements && gallery.achievements.length"
                class="experience-card"
              >
                <h3>Achievements</h3>
                <ul>
                  <li
                    v-for="achievement in gallery.achievements"
                    :key="achievement.slug"
                  >
                    <v-img
                      v-if="achievement.image"
                      :src="achievement.image | mediaSrc"
                      :alt="achievement.title"
                      aspect-ratio="1"
                      class="achievement-image"
                    ></v-img>
                    <v-icon v-else small>mdi-medal-outline</v-icon>
                    <span>{{ achievement.title }}</span>
                  </li>
                </ul>
              </div>

              <div
                v-if="gallery.firsts && gallery.firsts.length"
                class="experience-card"
              >
                <h3>Firsts</h3>
                <ul>
                  <li
                    v-for="first in gallery.firsts"
                    :key="first.slug"
                  >
                    <v-icon small>mdi-star-four-points-outline</v-icon>
                    <span>{{ first.title }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section
            v-if="relatedTrips.length"
            class="related-trips-section"
            aria-labelledby="related-trips-title"
          >
            <div class="kicker">Related Trips</div>
            <h2 id="related-trips-title" class="section-title">More Reckon Way Stories</h2>
            <v-row class="related-trips-grid">
              <v-col
                v-for="item in relatedTrips"
                :key="item.trip.slug"
                cols="12"
                md="4"
              >
                <TripCard
                  v-bind="tripCardForGallery(item.trip)"
                  :tags="item.sharedTags"
                  :to="{ name: 'reckon-way-gallery', params: { gallerySlug: item.trip.slug } }"
                  variant="compact"
                />
              </v-col>
            </v-row>
          </section>

          <div ref="photoGallery" id="photo-gallery" class="photo-gallery-heading">
            <div class="kicker">Photo Gallery</div>
            <h2 class="section-title">{{ gallery.title }} Moments</h2>
          </div>

          <ResponsiveMediaGallery
            class="media-gallery"
            :items="gallery.media"
            :aria-label="`${gallery.title} moments`"
          />
        </v-container>

        <v-container v-else>
          <v-btn
            outlined
            class="back-link mb-6"
            :to="{ name: 'reckon-way' }"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            The Reckon Way
          </v-btn>
          <div class="missing-gallery">Gallery not found.</div>
        </v-container>
      </div>
    </div>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import {
  allAdventureGalleries,
  galleryBySlug,
  getBlogPostSlug,
  getGalleryBlogPosts,
} from '../data/adventureGalleries';
import { reckonWayEventBySlug } from '../data/reckon-way/events';
import { adaptCorporateRetreat } from '../data/reckon-way/adaptCorporateRetreat';
import { experienceDNAFromFivePointScale } from '../data/reckon-way/experienceDNA';
import { getRelatedTrips, getTripTagIcon } from '../data/reckonWayTags';
import AppFooter from '../components/Footer.vue';
import AppHeader from '../components/Header.vue';
import EventRenderer from '../components/reckon-way/event/EventRenderer.vue';
import ResponsiveMediaGallery from '../components/reckon-way/media/ResponsiveMediaGallery.vue';
import TagPill from '../components/TagPill.vue';
import TripCard from '../components/TripCard.vue';
import TravelBlogArticle from '../components/TravelBlogArticle.vue';

export default {
  name: 'AdventureGalleryView',
  components: {
    AppFooter,
    AppHeader,
    EventRenderer,
    ResponsiveMediaGallery,
    TagPill,
    TravelBlogArticle,
    TripCard,
  },
  computed: {
    resolvedGallerySlug() {
      return this.$route.meta.eventSlug || this.$route.params.gallerySlug;
    },
    gallery() {
      return galleryBySlug[this.resolvedGallerySlug];
    },
    sharedEvent() {
      return reckonWayEventBySlug[this.resolvedGallerySlug]
        || adaptCorporateRetreat(this.gallery)
        || null;
    },
    blogPosts() {
      return getGalleryBlogPosts(this.gallery);
    },
    storiesComingSoon() {
      return this.gallery && this.gallery.storiesComingSoon === true;
    },
    releasedBlogPosts() {
      if (this.storiesComingSoon) {
        return [];
      }

      return this.blogPosts.filter((post) => post.released !== false);
    },
    inlineBlogPost() {
      if (!this.gallery || this.gallery.inlineBlog !== true) {
        return null;
      }

      return this.releasedBlogPosts.length === 1 ? this.releasedBlogPosts[0] : null;
    },
    blogLinks() {
      return this.releasedBlogPosts.map((post, index) => ({
        index,
        slug: getBlogPostSlug(post),
        title: post.title,
        lede: post.lede,
        route: {
          name: 'reckon-way-story',
          params: {
            gallerySlug: this.gallery.slug,
            storySlug: getBlogPostSlug(post),
          },
        },
      }));
    },
    storyActionIcon() {
      return this.storiesComingSoon ? 'mdi-clock-outline' : 'mdi-book-open-variant';
    },
    storyActionLabel() {
      if (this.storiesComingSoon) {
        return 'Stories Coming Soon';
      }

      return this.inlineBlogPost ? 'Trip Story' : 'Travel Stories';
    },
    atAGlanceItems() {
      if (!this.gallery || !this.gallery.atAGlance) {
        return [];
      }

      return Object.entries(this.gallery.atAGlance)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({
          key,
          label: this.formatAtAGlanceLabel(key),
          value: this.formatAtAGlanceValue(value),
        }));
    },
    experienceDnaScores() {
      if (!this.gallery || !this.gallery.experienceDNA) {
        return [];
      }

      const includeConference = this.gallery.meta === 'Conference'
        || this.gallery.tripType === 'Conference';

      return experienceDNAFromFivePointScale(
        this.gallery.experienceDNA,
        { includeConference },
      ).map((item) => ({
        key: item.id,
        label: item.label,
        value: item.score,
        width: `${item.score}%`,
      }));
    },
    hasExperienceMetadata() {
      if (!this.gallery) {
        return false;
      }

      return Boolean(
        this.gallery.experienceDNA
        || this.gallery.familyFriendly
        || (this.gallery.tags && this.gallery.tags.length)
        || (this.gallery.achievements && this.gallery.achievements.length)
        || (this.gallery.firsts && this.gallery.firsts.length)
        || (this.gallery.attendees && this.gallery.attendees.length),
      );
    },
    relatedTrips() {
      return getRelatedTrips(this.gallery, allAdventureGalleries);
    },
  },
  methods: {
    scrollToStories() {
      const storyTarget = this.$refs.storyContent || this.$refs.storyIndex;
      if (storyTarget) {
        storyTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    scrollToGallery() {
      if (this.$refs.photoGallery) {
        this.$refs.photoGallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    passportRoute(employeeSlug) {
      return {
        name: 'passport',
        params: {
          employeeSlug,
        },
      };
    },
    tagIcon(tag) {
      return getTripTagIcon(tag);
    },
    tripCardForGallery(gallery) {
      if (gallery.tripCard) {
        return gallery.tripCard;
      }

      const destination = gallery.destination
        ? [gallery.destination.city, gallery.destination.country].filter(Boolean).join(', ')
        : gallery.meta;

      return {
        tripSlug: gallery.slug,
        title: gallery.title,
        subtitle: gallery.subtitle || '',
        destination,
        date: gallery.dateLabel || '',
        tripType: gallery.tripType || gallery.meta,
        heroImage: gallery.heroImage,
        summary: gallery.summary || gallery.description,
        ctaText: 'View Adventure',
      };
    },
    formatAtAGlanceLabel(key) {
      return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatAtAGlanceValue(value) {
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      return value;
    },
  },
};
</script>

<style scoped>
.gallery-shell {
  background: #050505;
  padding-bottom: 60px;
}

.back-link {
  border-color: #424242 !important;
  color: #fff !important;
}

.gallery-hero {
  background-color: #050505;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  overflow: hidden;
}

.gallery-intro {
  margin: 30px 0 18px;
  max-width: 760px;
}

.kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.page-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 2.6rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 12px 0 16px;
}

.page-copy {
  color: #c9c9c9;
  font-size: 1.2rem;
  line-height: 1.55;
  margin-bottom: 0;
}

.trip-purpose {
  background: #121212;
  border-left: 3px solid #e23232;
  border-radius: 0 8px 8px 0;
  color: #d8d8d8;
  margin-top: 18px;
  padding: 16px 18px;
}

.trip-purpose strong {
  color: #fff;
  display: block;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  letter-spacing: 0;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.trip-purpose p {
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
}

.at-a-glance-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  margin-top: 18px;
}

.at-a-glance-item {
  background: #111;
  border: 1px solid #282828;
  border-radius: 8px;
  min-height: 84px;
  padding: 14px;
}

.at-a-glance-item span {
  color: #9f9f9f;
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.at-a-glance-item strong {
  color: #fff;
  display: block;
  font-family: "Oswald", serif;
  font-size: 1.45rem;
  letter-spacing: 0;
  line-height: 1.1;
  margin-top: 8px;
}

.gallery-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.action-button {
  border-color: #e23232 !important;
  color: #fff !important;
}

.story-index {
  border-bottom: 1px solid #2a2a2a;
  border-top: 1px solid #2a2a2a;
  margin-top: 32px;
  padding: 30px 0 34px;
  scroll-margin-top: 24px;
}

.inline-story {
  margin: 48px 0 18px;
  scroll-margin-top: 24px;
}

.trip-details-section {
  border-top: 1px solid #2a2a2a;
  margin-top: 42px;
  padding-top: 34px;
}

.gallery-trip-card {
  margin-top: 18px;
  max-width: 920px;
}

.related-trips-section {
  border-top: 1px solid #2a2a2a;
  margin-top: 42px;
  padding-top: 34px;
}

.related-trips-grid {
  margin-top: 14px;
}

.experience-taxonomy {
  border-top: 1px solid #2a2a2a;
  margin-top: 42px;
  max-width: 980px;
  padding-top: 34px;
}

.experience-dna-card,
.experience-card {
  background: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
}

.experience-dna-card {
  margin-top: 18px;
  padding: 22px;
}

.experience-dna-card h3,
.experience-group h3,
.experience-card h3 {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.3rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.15;
  margin: 0;
}

.dna-score-grid {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}

.dna-score-row {
  align-items: center;
  color: #fff;
  display: flex;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  font-weight: 800;
  justify-content: space-between;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.dna-score-row strong {
  color: #f0b13c;
  font-size: 0.92rem;
  margin-left: 14px;
}

.dna-score-track {
  background: #1d1d1d;
  border-radius: 999px;
  height: 8px;
  margin-top: 8px;
  overflow: hidden;
}

.dna-score-track span {
  background: linear-gradient(90deg, #e23232, #f0b13c);
  border-radius: inherit;
  display: block;
  height: 100%;
}

.family-note-card {
  align-items: flex-start;
  background: rgba(240, 177, 60, 0.1);
  border: 1px solid rgba(240, 177, 60, 0.42);
  border-radius: 8px;
  display: flex;
  gap: 14px;
  margin-top: 16px;
  padding: 18px;
}

.family-note-card .v-icon {
  color: #f0b13c;
  flex: 0 0 auto;
}

.family-note-card h3 {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.18rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.15;
  margin: 0;
}

.family-note-card p {
  color: #d8d8d8;
  font-size: 1rem;
  line-height: 1.5;
  margin: 7px 0 0;
}

.tag-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-chip-list {
  margin-top: 12px;
}

.tag-chip {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 7px;
  letter-spacing: 0;
  line-height: 1.2;
  padding: 7px 11px;
  text-transform: uppercase;
}

.tag-chip {
  background: rgba(226, 50, 50, 0.12);
  border: 1px solid rgba(226, 50, 50, 0.46);
  color: #fff;
}

.tag-chip .v-icon {
  color: #f0b13c;
}

.experience-group {
  margin-top: 26px;
}

.experience-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  margin-top: 24px;
}

.attendee-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-top: 12px;
}

.attendee-card {
  background: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  color: #fff;
  display: block;
  min-height: 128px;
  padding: 18px;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease;
}

.attendee-card:hover,
.attendee-card:focus {
  border-color: #e23232;
  color: #fff;
  transform: translateY(-2px);
}

.attendee-card span {
  color: #fff;
  display: block;
  font-family: "Oswald", serif;
  font-size: 1.18rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
}

.attendee-card strong {
  color: #c9c9c9;
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 8px;
}

.attendee-card small {
  align-items: center;
  color: #f0b13c;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 5px;
  letter-spacing: 0;
  line-height: 1.2;
  margin-top: 14px;
  text-transform: uppercase;
}

.attendee-card .v-icon {
  color: #e23232;
}

.experience-card {
  padding: 20px;
}

.experience-card ul {
  display: grid;
  gap: 10px;
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.experience-card li {
  align-items: flex-start;
  color: #d8d8d8;
  display: flex;
  font-size: 0.98rem;
  gap: 9px;
  line-height: 1.4;
}

.experience-card .v-icon {
  color: #f0b13c;
  flex: 0 0 auto;
  margin-top: 1px;
}

.achievement-image {
  background: #151515;
  border: 1px solid #2a2a2a;
  border-radius: 999px;
  flex: 0 0 26px;
  height: 26px;
  overflow: hidden;
  width: 26px;
}

.story-card-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  margin-top: 18px;
}

.story-card {
  background-color: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  color: #fff;
  display: flex;
  flex-direction: column;
  min-height: 230px;
  padding: 20px;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease;
}

.story-card:hover,
.story-card:focus {
  border-color: #e23232;
  color: #fff;
  transform: translateY(-2px);
}

.story-card-kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 0.86rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.story-card-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.45rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.12;
  margin: 12px 0 10px;
  overflow-wrap: anywhere;
}

.story-card-copy {
  color: #b8b8b8;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 18px;
}

.story-card-action {
  align-items: center;
  color: #fff;
  display: flex;
  font-family: "Oswald", serif;
  font-size: 1rem;
  font-weight: 900;
  gap: 8px;
  letter-spacing: 0;
  margin-top: auto;
  text-transform: uppercase;
}

.story-card-action .v-icon {
  color: #e23232;
}

.story-coming-soon {
  background: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  padding: 28px;
}

.story-coming-soon-copy {
  color: #c9c9c9;
  font-size: 1.08rem;
  line-height: 1.6;
  margin: 14px 0 0;
  max-width: 680px;
}

.blog-index {
  border-bottom: 1px solid #2a2a2a;
  border-top: 1px solid #2a2a2a;
  margin-top: 26px;
  max-width: 860px;
  padding: 20px 0;
}

.blog-index-kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.blog-index-links {
  display: grid;
  gap: 8px 24px;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  margin-top: 12px;
}

.blog-index-link {
  align-items: center;
  border-bottom: 1px solid #202020;
  color: #fff;
  display: flex;
  font-family: "Oswald", serif;
  font-size: 1.05rem;
  font-weight: 800;
  gap: 10px;
  letter-spacing: 0;
  line-height: 1.25;
  min-height: 46px;
  padding: 9px 0;
  text-decoration: none;
}

.blog-index-link .v-icon {
  color: #e23232;
  flex: 0 0 auto;
}

.blog-index-link span {
  overflow-wrap: anywhere;
}

.blog-index-link:hover,
.blog-index-link:focus {
  color: #e23232;
}

.travel-blog {
  border-top: 1px solid #2a2a2a;
  margin: 36px 0 0;
  max-width: 860px;
  padding-top: 36px;
  scroll-margin-top: 24px;
}

.blog-kicker {
  color: #e23232;
  font-family: "Oswald", serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.blog-title,
.section-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.1;
}

.blog-title {
  font-size: 2.1rem;
  margin: 10px 0 14px;
}

.blog-lede {
  color: #d5d5d5;
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 30px;
}

.blog-image-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin: 28px 0 34px;
}

.blog-section-image-grid {
  margin: 22px 0 4px;
}

.blog-image-card {
  background-color: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  margin: 0;
  overflow: hidden;
}

.blog-image-card-wide {
  grid-column: 1 / -1;
}

.blog-image {
  display: block;
  height: auto;
  width: 100%;
}

.blog-image-caption {
  border-top: 1px solid #242424;
  color: #b8b8b8;
  font-size: 0.95rem;
  line-height: 1.45;
  margin: 0;
  padding: 12px 14px;
}

.blog-section {
  margin-top: 30px;
}

.blog-section-title {
  border-left: 5px solid #e23232;
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
  margin-bottom: 14px;
  padding-left: 16px;
}

.blog-paragraph {
  color: #c9c9c9;
  font-size: 1.08rem;
  line-height: 1.75;
  margin-bottom: 16px;
}

.photo-gallery-heading {
  border-top: 1px solid #2a2a2a;
  margin-top: 42px;
  padding-top: 34px;
}

.section-title {
  font-size: 2rem;
  margin: 10px 0 0;
}

.media-gallery {
  margin-top: 18px;
}

.missing-gallery {
  color: #fff;
  font-size: 1.4rem;
}

@media (max-width: 600px) {
  .page-title {
    font-size: 2rem;
  }

  .page-copy {
    font-size: 1.05rem;
  }

  .blog-title,
  .section-title {
    font-size: 1.75rem;
  }

  .blog-lede {
    font-size: 1.1rem;
  }

  .blog-index-links {
    grid-template-columns: 1fr;
  }

  .blog-image-grid {
    grid-template-columns: 1fr;
  }
}
</style>
